import type { CSSProperties, FC, ReactNode } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";

const useSize = (target: React.RefObject<HTMLElement | null>) => {
  const [size, setSize] = useState<DOMRectReadOnly | null>(null);
  useEffect(() => {
    const el = target.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setSize(entries[0].contentRect);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);
  return size;
};

const BG = "#080c14";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DialContainer = styled.div`
  position: relative;
  user-select: none;
  -webkit-user-select: none;
`;

const Gradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 40%, ${BG} 75%);
  z-index: 10;
  pointer-events: none;
  border-radius: 50%;
`;

const ChildWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const HandleContainer = styled.div<{ angle: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  transform: rotate(${(p) => p.angle}deg);
  z-index: 30;
`;

const Handle = styled.div<{ radius: number }>`
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #38bdf8;
  box-shadow: 0 0 15px 0px rgba(56, 189, 248, 0.5);
  top: ${(p) => -p.radius}px;
  left: -8px;
  cursor: grab;
  touch-action: none;
  &:active {
    cursor: grabbing;
  }
`;

const StyledDial = styled.div<{ size: number }>`
  width: ${(p) => p.size}px;
  height: ${(p) => p.size}px;
  border-radius: 50%;
  background: #0d1a2e;
  border: 2px solid rgba(255, 255, 255, 0.08);
  box-shadow: 6px 9px 40px 0px rgba(0, 0, 0, 0.6), -6px -6px 16px 0px rgba(255, 255, 255, 0.03);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  touch-action: none;
  &:active {
    cursor: grabbing;
  }
`;

const Outer = styled.div<{ size: number }>`
  width: ${(p) => p.size}px;
  height: ${(p) => p.size}px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0d1a2e 0%, #0a1628 100%);
  border: 2px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const SubTextWrapper = styled.div`
  position: absolute;
  bottom: 18%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 25;
  pointer-events: none;
  text-align: center;
  width: 100%;
`;

const toDeg = (rad: number) => (rad * 180) / Math.PI;

const getAngle = (dx: number, dy: number) => toDeg(Math.atan2(dy, dx));

const getNewPoint = (cx: number, cy: number, r: number, angleDeg: number) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

export type DialPropsT = {
  value: number;
  min?: number;
  max?: number;
  increment?: number;
  size?: number;
  onChange?: (value: number) => void;
  children?: ReactNode;
  subText?: ReactNode;
  style?: CSSProperties;
};

const Dial: FC<DialPropsT> = ({
  value,
  min = 0,
  max = 360,
  increment = 1,
  size: sizeProp,
  onChange,
  children,
  subText,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useSize(containerRef);
  const dialRef = useRef<HTMLDivElement>(null);

  const resolvedSize = sizeProp ?? (containerSize?.width ? Math.min(containerSize.width, 400) : 300);
  const outerSize = resolvedSize;
  const innerSize = resolvedSize * 0.82;
  const handleRadius = innerSize * 0.47;

  const clampedValue = Math.min(max, Math.max(min, value));
  const startAngle = -150;
  const angleDeg = startAngle + ((clampedValue - min) / (max - min)) * 270;

  const isDragging = useRef(false);
  const lastAngle = useRef<number | null>(null);
  const accumulated = useRef(clampedValue);

  useEffect(() => {
    accumulated.current = clampedValue;
  }, [clampedValue]);

  const getDialCenter = useCallback(() => {
    const rect = dialRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      isDragging.current = true;
      accumulated.current = clampedValue;
      const center = getDialCenter();
      lastAngle.current = getAngle(e.clientX - center.x, e.clientY - center.y);
    },
    [clampedValue, getDialCenter],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current || lastAngle.current === null) return;
      const center = getDialCenter();
      const currentAngle = getAngle(e.clientX - center.x, e.clientY - center.y);
      let delta = currentAngle - lastAngle.current;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      const rawDelta = delta / (270 / (max - min));
      accumulated.current += rawDelta;
      const snapped = Math.round(accumulated.current / increment) * increment;
      const clamped = Math.min(max, Math.max(min, snapped));
      lastAngle.current = currentAngle;
      onChange?.(clamped);
    },
    [getDialCenter, max, min, increment, onChange],
  );

  const onPointerUp = useCallback(() => {
    isDragging.current = false;
    lastAngle.current = null;
  }, []);

  const notchCount = Math.floor((max - min) / increment);
  const notchStep = 270 / notchCount;
  const svgSize = innerSize;
  const svgCenter = svgSize / 2;
  const notchOuterR = svgSize / 2 - 2;
  const notchInnerR = svgSize / 2 - 8;

  const notches = Array.from({ length: notchCount + 1 }, (_, i) => {
    const deg = -150 + i * notchStep;
    const outer = getNewPoint(svgCenter, svgCenter, notchOuterR, deg);
    const inner = getNewPoint(svgCenter, svgCenter, notchInnerR, deg);
    const isActive = i <= Math.round((clampedValue - min) / increment);
    return { outer, inner, isActive };
  });

  return (
    <Wrapper ref={containerRef} style={style}>
      <DialContainer>
        <Outer size={outerSize}>
          <StyledDial
            ref={dialRef}
            size={innerSize}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <svg width={svgSize} height={svgSize} style={{ position: "absolute", top: 0, left: 0 }}>
              <defs>
                <filter id="notch-glow-active" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="notch-glow-dim" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="1.2" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              {notches.map((n) => (
                <line
                  key={`${n.outer.x}-${n.outer.y}`}
                  x1={n.outer.x}
                  y1={n.outer.y}
                  x2={n.inner.x}
                  y2={n.inner.y}
                  stroke={n.isActive ? "#38bdf8" : "rgba(56,189,248,0.18)"}
                  strokeWidth={n.isActive ? 2.5 : 1}
                  strokeLinecap="round"
                  filter={n.isActive ? "url(#notch-glow-active)" : "url(#notch-glow-dim)"}
                />
              ))}
            </svg>
            <Gradient />
            <ChildWrapper>{children}</ChildWrapper>
            <SubTextWrapper>{subText}</SubTextWrapper>
          </StyledDial>
          <HandleContainer angle={angleDeg}>
            <Handle
              radius={handleRadius}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
          </HandleContainer>
        </Outer>
      </DialContainer>
    </Wrapper>
  );
};

export { Dial };
